// Copyright 2026 AllSpice. All rights reserved.

import {waitForRender} from '../../../domutils.ts';
import {promiseWithResolvers} from '../../lib/promise.ts';

type PageNavigationRoot = ParentNode | null | undefined;

type PageNavigationElements = {
  pageRow: HTMLElement | null;
  pageContainer: HTMLElement | null;
};

function findScopedElement(root: PageNavigationRoot, selector: string): HTMLElement | null {
  if (!root) {
    return null;
  }

  if (root instanceof HTMLElement && root.matches(selector)) {
    return root;
  }

  return root.querySelector<HTMLElement>(selector);
}

function getPageNavigationElements(root: PageNavigationRoot, pageId: string): PageNavigationElements {
  if (!root || !pageId) {
    return {
      pageRow: null,
      pageContainer: null,
    };
  }

  const pageRow = findScopedElement(root, `[data-accordion-head="${CSS.escape(pageId)}"]`);
  const pageContents = pageRow?.nextElementSibling;
  const pageContainer = pageContents instanceof HTMLElement ?
    pageContents :
    findScopedElement(root, `[data-accordion-content="${CSS.escape(pageId)}"]`);

  return {
    pageRow,
    pageContainer,
  };
}

/**
 * Expand the accordion section for the given page (if collapsed) and scroll
 * it into view.  Used by both the changes diff list (DiffItem.vue) and the
 * multi-page schematic component search to navigate between pages of a
 * multi-page schematic in project view.
 */
export async function openAndScrollToPage(root: PageNavigationRoot, pageId: string): Promise<void> {
  const {pageRow, pageContainer} = getPageNavigationElements(root, pageId);
  if (!pageContainer) {
    console.debug(`No page container found with page id ${pageId}`);
    return;
  }

  // expand the row if it's not expanded
  if (pageRow?.ariaExpanded === 'false') {
    // set up the transitionend event listener as a promise we can await
    const {promise: transitionEndPromise, resolve: transitionEndResolve} = promiseWithResolvers();
    pageContainer.addEventListener('transitionend', transitionEndResolve, {once: true});

    // TODO: Toggle accordion state directly once this view exposes it via
    // Vue state instead of relying on a synthetic click.
    pageRow.dispatchEvent(new MouseEvent('click', {bubbles: true}));
    await transitionEndPromise;
  }

  pageContainer.scrollIntoView({
    block: 'start',
  });
  await waitForRender();
}

export function findPageContainer(root: PageNavigationRoot, pageId: string): HTMLElement | null {
  return getPageNavigationElements(root, pageId).pageContainer;
}

export function queryPageScopedElements(root: PageNavigationRoot, pageId: string | null | undefined, selector: string): Element[] {
  if (!root) {
    return [];
  }

  if (pageId) {
    const pageContainer = findPageContainer(root, pageId);
    const pageElements = pageContainer ? Array.from(pageContainer.querySelectorAll(selector)) : [];
    if (pageElements.length > 0) {
      return pageElements;
    }
  }

  return Array.from(root.querySelectorAll(selector));
}

export async function waitForPageSvg(root: PageNavigationRoot, pageId: string, timeoutMs = 5000): Promise<SVGSVGElement | null> {
  const start = Date.now();

  while (Date.now() - start < timeoutMs) {
    const container = findPageContainer(root, pageId);
    const svg = container?.querySelector<SVGSVGElement>('.svg-wrapper svg');
    if (svg) {
      return svg;
    }

    await new Promise((resolve) => setTimeout(resolve, 50));
  }

  return null;
}
